import React from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {WeatherService} from '../api';
import {WeatherForecast} from '../api/types';
import {
  EMPTY_FORECAST,
  getForecastModelsByGenericStationId,
  getSelectedForecastModelByGenericStationId,
  getForecastSelectionByGenericStationId,
  getStationForecastData,
  getWeatherRawForecastData,
  pushStationForecast,
  setWeatherForecastModels,
} from '../store';
import {Dates} from '../utils';
import {NormalizedForecastModel} from '../utils/Forecast/types';
import {useUserUnits} from './userContext';
import useApiClient from './useApiClient';
import useParamSelector from './useParamSelector';

// Per-session fetch timestamps, keyed by stationId. Multiple components on
// the same screen call `useStationForecast(stationId)` and would otherwise
// each fire their own GET; this collapses them to one request per FETCH_TTL_MS
// window without preventing later mounts from picking up new fields.
const FETCH_TTL_MS = 60_000;

/**
 * What became of an `updateGivenForecastModel` call: the forecast was stored,
 * the request failed, or it was not made because another was running.
 */
type ForecastFetchOutcome = 'fetched' | 'failed' | 'skipped';
const lastFetchedAt = new Map<number, number>();

const useStationForecast = (stationId: number) => {
  const {fetch} = useApiClient();
  const units = useUserUnits();
  const dispatch = useDispatch();
  const [isUpdating, setIsUpdating] = React.useState(false);

  const forecastModelList: NormalizedForecastModel[] = useParamSelector(
    getForecastModelsByGenericStationId,
    stationId,
  );

  const forecastModel: NormalizedForecastModel = useParamSelector(
    getSelectedForecastModelByGenericStationId,
    stationId,
  );

  // Why that model, not just which — see deriveForecastSelection.
  const forecastSelection = useParamSelector(
    getForecastSelectionByGenericStationId,
    stationId,
  );

  const forecastData: WeatherForecast = useParamSelector(
    getStationForecastData,
    stationId,
  );

  const fullForecastData = useSelector(getWeatherRawForecastData);

  const getForecastDataForModel = React.useCallback(
    (model: NormalizedForecastModel) => {
      const key = model
        ? model.serverId.meshSize
          ? `${stationId}-${model.serverId.forecastModel}-${model.serverId.meshSize}`
          : Object.keys(fullForecastData).find((k) =>
              k.startsWith(`${stationId}-${model.serverId.forecastModel}-`),
            )
        : Object.keys(fullForecastData).find((k) =>
            k.startsWith(`${stationId}`),
          );
      if (!key || !(key in fullForecastData)) {
        return EMPTY_FORECAST;
      } else {
        return fullForecastData[key];
      }
    },
    [fullForecastData, stationId],
  );

  const isUpdateRequired = React.useCallback(
    (data?: WeatherForecast) => {
      const value = data || forecastData;
      if (
        value.computed_at === null ||
        Dates.timeDiffUtcWithNowInMin(value.computed_at) > 30
      ) {
        return true;
      }
      return false;
    },
    [forecastData],
  );

  const updateForecastModels = React.useCallback(() => {
    lastFetchedAt.set(stationId, Date.now());
    fetch(WeatherService.stationWeatherForecastModelList(stationId), {
      ignore404: true,
    })
      .then((data) => {
        dispatch(
          setWeatherForecastModels({
            stationId: stationId,
            forecastModels: data,
          }),
        );
      })
      .catch(() => {});
  }, [dispatch, fetch, stationId]);

  // Refresh the list periodically: the persisted store renders immediately,
  // but treating it as authoritative means new backend-side models (or new
  // fields on existing ones, like coverage_image_*) never reach an existing
  // user. TTL-gated so the multiple components mounted on the same screen
  // collapse to one request per stationId per window.
  React.useEffect(() => {
    if (!stationId) return;
    const lastAt = lastFetchedAt.get(stationId) ?? 0;
    if (Date.now() - lastAt > FETCH_TTL_MS) {
      updateForecastModels();
    }
  }, [stationId, updateForecastModels]);

  const updateGivenForecastModel = React.useCallback(
    async (model: NormalizedForecastModel): Promise<ForecastFetchOutcome> => {
      if (isUpdating || !stationId) {
        // Prevent multiple simultaneous calls or invalid params. Said, not
        // swallowed: a skipped request is not "this model has no forecast".
        return 'skipped';
      }

      setIsUpdating(true);
      try {
        const data: WeatherForecast = await fetch(
          WeatherService.stationWeatherForecast(
            stationId,
            model ? model.serverId.forecastModel : undefined,
            model ? model.serverId.meshSize : undefined,
          ),
          // The daily forecast description is formatted server-side, so the
          // backend needs the user's units. Authenticated users are resolved
          // from their profile, but anonymous viewers are only conveyed via
          // this header — without it the description falls back to metric.
          {
            ignore404: true,
            headers: {'X-SmartMixin-Units': JSON.stringify(units)},
          },
        );

        dispatch(
          pushStationForecast({
            stationId,
            forecastModel: data.metadata.forecast_model,
            meshSize: data.metadata.mesh_size,
            data,
          }),
        );
        return 'fetched';
      } catch {
        // No alert here (the screens say it); the caller is told.
        return 'failed';
      } finally {
        setIsUpdating(false);
      }
    },
    [fetch, units, stationId, dispatch, isUpdating],
  );

  const updateForecast = React.useCallback(() => {
    updateGivenForecastModel(forecastModel);
  }, [forecastModel, updateGivenForecastModel]);

  const memoizedReturn = React.useMemo(
    () => ({
      updateForecast,
      updateForecastModels,
      forecastModelList,
      forecastModel,
      forecastSelection,
      forecastData,
      isUpdateRequired,
      getForecastDataForModel,
      updateGivenForecastModel,
      isUpdating,
    }),
    [
      updateForecast,
      updateForecastModels,
      forecastModelList,
      forecastModel,
      forecastSelection,
      forecastData,
      isUpdateRequired,
      getForecastDataForModel,
      updateGivenForecastModel,
      isUpdating,
    ],
  );

  return memoizedReturn;
};

export default useStationForecast;
